import { z } from "zod";

import { assetSchema } from "./asset.schema";
import { emailSchema, usernameSchema } from "./common.schema";
import { twrModeSchema } from "./portfolio.schema";

// =============================================================================
// User Schema
// =============================================================================

export const userSchema = z.object({
  user_id: z.string(),
  name: z.string(),
  username: z.string().optional(),
  email: z.string().email(),
  base_currency_id: z.string().optional(),
  is_admin: z.boolean().optional(),
});

export type User = z.infer<typeof userSchema>;

// =============================================================================
// User Profile Form Schema
// =============================================================================

export const userProfileFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name is too long"),
  username: usernameSchema.optional().or(z.literal("")),
  email: emailSchema,
});

export const editProfileFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name is too long"),
  username: usernameSchema.optional().or(z.literal("")),
});

export type UserProfileFormData = z.infer<typeof userProfileFormSchema>;
export type EditProfileFormData = z.infer<typeof editProfileFormSchema>;

// =============================================================================
// Watchlist Schemas
// =============================================================================

export const watchlistSchema = z.object({
  watchlist_id: z.string(),
  user_id: z.string(),
  name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  items_count: z.number().optional(),
});

export const watchlistItemSchema = z.object({
  item_id: z.string(),
  watchlist_id: z.string(),
  user_id: z.string(),
  asset_id: z.string(),
  note: z.string().optional(),
  target_price: z.number().optional(),
  position: z.number().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  asset: assetSchema.optional(),
  latest_price: z.number().optional(),
  latest_price_currency: z.string().optional(),
  daily_change_percent: z.number().optional(),
});

export type Watchlist = z.infer<typeof watchlistSchema>;
export type WatchlistItem = z.infer<typeof watchlistItemSchema>;

// =============================================================================
// Watchlist Form Schemas
// =============================================================================

export const watchlistNameSchema = z
  .string()
  .min(1, "Name is required")
  .max(50, "Name must be at most 50 characters")
  .trim();

export const createWatchlistFormSchema = z.object({
  name: watchlistNameSchema,
});

export const renameWatchlistFormSchema = z.object({
  name: watchlistNameSchema,
});

export const watchlistItemFormSchema = z.object({
  asset_id: z.string().min(1, "Asset is required"),
  note: z.string().max(500, "Note is too long").optional(),
  target_price: z.number().positive("Target price must be positive").optional(),
});

export const editWatchlistItemFormSchema = z.object({
  note: z.string().max(500, "Note is too long").optional(),
  target_price: z
    .union([
      z.number().positive("Target price must be positive"),
      z.nan(),
      z.literal(""),
    ])
    .optional()
    .transform((val) => {
      if (val === "" || (typeof val === "number" && isNaN(val))) {
        return undefined;
      }
      return val as number | undefined;
    }),
});

export type CreateWatchlistFormData = z.infer<typeof createWatchlistFormSchema>;
export type RenameWatchlistFormData = z.infer<typeof renameWatchlistFormSchema>;
export type WatchlistItemFormData = z.infer<typeof watchlistItemFormSchema>;
export type EditWatchlistItemFormData = z.infer<typeof editWatchlistItemFormSchema>;

// =============================================================================
// Import Schemas
// =============================================================================

export const missingAssetSchema = z.object({
  row_number: z.number(),
  identifier: z.string(),
  description: z.string(),
});

export const skippedTransactionSchema = z.object({
  row_number: z.number(),
  category: z.string(),
  type: z.string(),
  reason: z.string(),
});

export const importErrorSchema = z.object({
  row_number: z.number(),
  message: z.string(),
});

export const importResultSchema = z.object({
  message: z.string(),
  transactions_imported: z.number(),
  transactions_skipped: z.number(),
  missing_assets: z.array(missingAssetSchema),
  skipped_transactions: z.array(skippedTransactionSchema).default([]),
  detected_mode: twrModeSchema.optional(),
  errors: z.array(importErrorSchema),
});

export type MissingAsset = z.infer<typeof missingAssetSchema>;
export type SkippedTransaction = z.infer<typeof skippedTransactionSchema>;
export type ImportError = z.infer<typeof importErrorSchema>;
export type ImportResult = z.infer<typeof importResultSchema>;

// =============================================================================
// Feedback/Contact Form Schemas
// =============================================================================

export const feedbackCategorySchema = z.enum([
  "bug",
  "feature",
  "question",
  "other",
]);

export const feedbackFormSchema = z.object({
  category: z.string().min(1, "Please select a category"),
  message: z
    .string()
    .min(10, "Message must be at least 10 characters")
    .max(2000, "Message must be at most 2000 characters"),
});

export const contactFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: emailSchema,
  message: z.string().min(10, "Message must be at least 10 characters"),
});

export const waitlistFormSchema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  email: emailSchema,
});

export type FeedbackCategory = z.infer<typeof feedbackCategorySchema>;
export type FeedbackFormData = z.infer<typeof feedbackFormSchema>;
export type ContactFormData = z.infer<typeof contactFormSchema>;
export type WaitlistFormData = z.infer<typeof waitlistFormSchema>;
